'use client';

import { type FC, useState } from 'react';
import {
  Dialog,
  Flex,
  Text,
  Button,
  Icon,
  ConnectAppDialog,
} from '@/_components';
import styles from './ConnectedAppsDialog.module.css';
import type { ConnectedAppsDialogProps } from './ConnectedAppsDialog.types';
import type { AppInfo } from '../ConnectAppDialog/ConnectAppDialog.types';

const appsToShow: AppInfo[] = [
  {
    id: 'karbon',
    name: 'Karbon',
    description: 'Connect Karbon to manage your tasks.',
    image: '/images/apps/karbonhq_logo.jpeg',
  },
  {
    id: 'click-up',
    name: 'ClickUp',
    description: 'Connect ClickUp to manage your projects and tasks.',
    image: '/images/apps/clickup_app_logo.jpeg',
  },
  {
    id: 'ms-sharepoint',
    name: 'Microsoft Sharepoint',
    description: 'Connect Microsoft Sharepoint to sync documents and files.',
    image: '/images/apps/sharepoint_logo.png',
  },
];

export const ConnectedAppsDialog: FC<ConnectedAppsDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const [isConnectAppDialogOpen, setIsConnectAppDialogOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState<AppInfo | null>(null);

  const handleOpenConnectAppDialog = (app: AppInfo) => {
    setSelectedApp(app);
    setIsConnectAppDialogOpen(true);
  };

  const handleCloseConnectAppDialog = () => {
    setIsConnectAppDialogOpen(false);
    setSelectedApp(null);
  };

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        title="Integrations App"
        isExtraWide
      >
        <Dialog.Content>
          <Flex className={styles.dialogContent}>
            <Flex className={styles.appsGrid}>
              {appsToShow.map((app) => (
                <Flex key={app.id} isColumn gap={8} className={styles.appCard}>
                  <Flex isRow isBetween isYCentered>
                    <Flex className={styles.appInfo}>
                      <div className={styles.appIconContainer}>
                        {app.image ? (
                          <img
                            src={app.image}
                            alt={`${app.name} logo`}
                            className={styles.appImage}
                          />
                        ) : (
                          <Icon name="browser" size={24} />
                        )}
                      </div>
                      <Text fontWeight="medium">{app.name}</Text>
                    </Flex>

                    <Button
                      value="Connect"
                      size="small"
                      intent="secondary"
                      onClick={() => handleOpenConnectAppDialog(app)}
                    />
                  </Flex>

                  <Text color="secondary" type="small">
                    {app.description}
                  </Text>
                </Flex>
              ))}
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog>

      {selectedApp && (
        <ConnectAppDialog
          isOpen={isConnectAppDialogOpen}
          onClose={handleCloseConnectAppDialog}
          app={selectedApp}
        />
      )}
    </>
  );
};
